import React, {
    memo, useCallback, useEffect, useMemo, useState,
} from 'react';
import { FormikValues, useField, useFormikContext } from "formik"
import { MuiPickersUtilsProvider, TimePicker } from '@material-ui/pickers';
import LuxonUtils from '@date-io/luxon';
import { DateTime } from 'luxon';
import { TextField, Typography, makeStyles } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock } from '@fortawesome/pro-light-svg-icons/faClock';
import { getAmPm } from '../../Helpers/DateTimeHelper';
import { useLocale } from '../../Modules/Localization/useLocale';
import { useLocalization } from '@fluent/react';
import AsteriskComponent from '../Layout/Asterisk';

const useFormControlStyles = makeStyles((theme) => ({
    mb_1: { marginBottom: theme.spacing(1) },
    timePickerWrap: {
        '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: `${theme.palette.error.main} !important`
        },
    },
}));

interface ITimePickerControlProps {
    name: string;
    dateFieldName: string;
    openingHours: Array<{
        weekDayNumber: number;
        hours: { open: string; close: string };
    }>;
    label?: string;
    placeholder?: string;
    isFloatingLabel?: boolean;
    required?: boolean;
}

function TimePickerControl({
                               name,
                               dateFieldName,
                               openingHours,
                               label,
                               placeholder,
                               isFloatingLabel,
                               required,
                           }: ITimePickerControlProps) {
    const classes = useFormControlStyles();
    const { setFieldValue, setFieldTouched, values } = useFormikContext<FormikValues>();
    const [field, meta] = useField(name);
    const [open, setOpen] = useState(false);
    const [unavailableTime, setUnavailableTime] = useState(false)

    const { l10n } = useLocalization();
    const locale   = useLocale();
    const isHour12 = getAmPm(l10n, locale);

    const time = useMemo(
        () => (field.value ? DateTime.fromISO(field.value) : null),
        [field.value],
    );

    const selectedDate: DateTime | null = useMemo(() => {
        const iso = values?.[dateFieldName] as string | undefined;
        setUnavailableTime(false)
        return iso ? DateTime.fromISO(iso) : null;
    }, [values, dateFieldName]);

    const dayWindow = useMemo(() => {
        if (!selectedDate) { return null; }

        const cfg = openingHours.find((h) => h.weekDayNumber === selectedDate.weekday);
        if (!cfg) { return null; }

        const [oH, oM] = cfg.hours.open.split(':').map(Number);
        const [cH, cM] = cfg.hours.close.split(':').map(Number);

        return {
            openHour: oH,
            openMinute: oM,
            closeHour: cH,
            closeMinute: cM,
        };
    }, [selectedDate, openingHours]);

    const error = useMemo(() => {
        return unavailableTime && !time || meta.touched && Boolean(meta.error)
    }, [time, unavailableTime, meta.touched, meta.error])

    const formatToAmPm = useCallback((timeString: string) => {
        if (!timeString) return '';

        const [hours, minutes] = timeString.split(':').map(Number);
        const period = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;

        return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
    }, []);

    const errorText = useMemo(() => {
        if (!selectedDate && !time && meta.touched) {
            return l10n.getString("delivery-form-meeting-field-delivery-time-need-date", undefined, "Vælg venligst en leveringsdato først.")
        }

        if (unavailableTime && !time) {
            const cfg = openingHours.find((h) => h.weekDayNumber === selectedDate?.weekday);

            if (cfg) {
                const hours = {
                    from: formatToAmPm(cfg?.hours.open),
                    to: formatToAmPm(cfg?.hours.close)
                };

                if (hours?.from && hours?.to) {
                   return l10n.getString("delivery-form-meeting-field-delivery-time-available", { from: hours.from, to: hours.to }, "Levering på dette tidspunkt er ikke tilgængelig")
                }
            }
        }

        if (meta.touched) {
            return meta.error
        }

        return undefined
    }, [time, unavailableTime, meta.touched, meta.error, selectedDate, openingHours, formatToAmPm])

    const handleChange = useCallback((val: DateTime | null) => {
        if (val && dayWindow) {
            const rounded = val
                .set({ minute: val.minute, second: 0, millisecond: 0 });

            if (
                rounded.hour  <  dayWindow.openHour  ||
                rounded.hour  >  dayWindow.closeHour ||
                (rounded.hour === dayWindow.openHour  && rounded.minute <  dayWindow.openMinute) ||
                (rounded.hour === dayWindow.closeHour && rounded.minute >= dayWindow.closeMinute)
            ) {
                setUnavailableTime(true)
                return;
            }

            setUnavailableTime(false)
            setFieldValue(name, rounded.toISOTime());
        }
        setOpen(false);
    }, [name, setFieldValue, setFieldTouched, dayWindow]);

    useEffect(() => { if (!open) { setFieldTouched(name, true, true); } }, [open]);

    return (
        <div className={classes.timePickerWrap}>
            {label && !isFloatingLabel && (
                <Typography variant="body1" className={classes.mb_1}>
                    {label}
                    <AsteriskComponent required={Boolean(required)} />
                </Typography>
            )}

            <MuiPickersUtilsProvider utils={LuxonUtils} locale={locale}>
                <TimePicker
                    open={open}
                    onOpen={() => !selectedDate ? setFieldTouched(field.name, true, true) : setOpen(true)}
                    onClose={() => setOpen(false)}
                    value={time}
                    onChange={handleChange}
                    onAccept={handleChange}
                    autoOk={true}
                    minutesStep={5}
                    ampm={isHour12}
                    openTo="hours"
                    views={['hours', 'minutes']}
                    TextFieldComponent={(p) => (
                        <TextField
                            {...p}
                            value={time?.toFormat(isHour12 ? 'hh:mm a' : 'HH:mm') ?? ''}
                            inputProps={{ readOnly: true }}
                            variant="outlined"
                            fullWidth={true}
                            placeholder={placeholder}
                            label={!placeholder && label ? label : ''}
                            error={error}
                            helperText={errorText}
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                                endAdornment: <FontAwesomeIcon icon={faClock} />,
                            }}
                        />
                    )}
                />
            </MuiPickersUtilsProvider>
        </div>
    );
}

export default memo(TimePickerControl);
